import { createContext, useContext, useEffect, useMemo, useState } from 'react';

export type ConsentCategories = {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
};

type ConsentContextValue = {
  consent: ConsentCategories | null;
  setConsent: (c: ConsentCategories) => void;
  acceptAll: () => void;
  rejectAll: () => void;
  openManager: () => void;
  closeManager: () => void;
  managerOpen: boolean;
};

const ConsentContext = createContext<ConsentContextValue | undefined>(undefined);

const COOKIE_NAME = 'consent.v1';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 180;

function readConsentCookie(): ConsentCategories | null {
  try {
    const cookies = document.cookie.split(';').map((c) => c.trim());
    const found = cookies.find((c) => c.startsWith(COOKIE_NAME + '='));
    if (!found) return null;
    const raw = decodeURIComponent(found.split('=')[1] || '');
    const parsed = JSON.parse(raw);
    if (typeof parsed === 'object' && parsed) {
      return { necessary: true, analytics: Boolean(parsed.analytics), marketing: Boolean(parsed.marketing) };
    }
  } catch {}
  return null;
}

function writeConsentCookie(value: ConsentCategories): void {
  const payload = encodeURIComponent(JSON.stringify({ analytics: value.analytics, marketing: value.marketing }));
  document.cookie = `${COOKIE_NAME}=${payload}; Max-Age=${COOKIE_MAX_AGE}; SameSite=Lax; Path=/`;
}

export function ConsentProvider({ children }: { children: React.ReactNode }) {
  const [consent, setConsentState] = useState<ConsentCategories | null>(null);
  const [managerOpen, setManagerOpen] = useState(false);

  useEffect(() => {
    setConsentState(readConsentCookie());
  }, []);

  const setConsent = (c: ConsentCategories) => {
    setConsentState(c);
    writeConsentCookie(c);
  };

  const value = useMemo<ConsentContextValue>(() => ({
    consent,
    setConsent,
    acceptAll: () => setConsent({ necessary: true, analytics: true, marketing: true }),
    rejectAll: () => setConsent({ necessary: true, analytics: false, marketing: false }),
    openManager: () => setManagerOpen(true),
    closeManager: () => setManagerOpen(false),
    managerOpen,
  }), [consent, managerOpen]);

  return (
    <ConsentContext.Provider value={value}>
      {children}
    </ConsentContext.Provider>
  );
}

export function useConsent(): ConsentContextValue {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error('useConsent must be used within ConsentProvider');
  return ctx;
}


